import React from 'react';
import { useHistory } from 'react-router-dom';
import { useAuthContext } from '../../contexts/Auth';
import { useOnboardingContext } from '../../contexts/Onboarding';
import { useSnackbarContext } from '../../contexts/Snackbar';
import { useEventTrackerContext } from '../../contexts/EventTracker';
import {
  login,
  saveSignUpData,
  resendVerifyEmailCode,
  getUserAttributes,
} from '../../utils';
import { Field, Form, Formik } from 'formik';
import { TextField } from 'formik-material-ui';
import { Auth } from 'aws-amplify';
import * as yup from 'yup';
import companyInfo from '../../themes/themes';
import makeStyles from '@material-ui/core/styles/makeStyles';
import LoadingButton from '../lowLevel/LoadingButton';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: 350,
    color: 'black',
  },
  header: {
    color: 'black',
    fontSize: 'xx-large',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('sm')]: {
      whiteSpace: 'normal',
    },
  },
  subHeader: { fontSize: '1.2em' },
  inputLabel: {
    color: 'black',
    fontWeight: 'bold',
    marginTop: theme.spacing(2),
  },
  field: {
    borderRadius: 4,
    '& .MuiOutlinedInput-root': { background: 'white' },
    '& .MuiOutlinedInput-input': { padding: theme.spacing(1.5) },
  },
  verifyEmailButton: { marginTop: theme.spacing(2) },
  resendCode: { marginTop: theme.spacing(2), cursor: 'pointer' },
}));

export async function trackEmailVerified(
  trackEvent: (event: Record<string, unknown>) => void,
) {
  const user = await getUserAttributes();
  const userId = user['cognito:username'];
  const email = user.email;
  const firstName = user['custom:first_name'];
  const lastName = user['custom:last_name'];

  trackEvent({
    event: 'email_verified',
    user_id: userId,
    user: email,
    user_name: `${firstName} ${lastName}`,
  });
}

export default function VerifyEmail({
  email = null,
  password = null,
  signup = false,
}) {
  const classes = useStyles();
  const snackbar = useSnackbarContext();
  const history = useHistory();
  const { trackEvent } = useEventTrackerContext();
  const { signIn, setUser } = useAuthContext();
  const { setHasCompletedOnboarding, setActiveStep } = useOnboardingContext();
  const theme = process.env.REACT_APP_THEME;

  async function verifyEmail(code: string) {
    await Auth.confirmSignUp(email, code);
  }

  async function handleSubmit(values: { code: string }) {
    try {
      await verifyEmail(values.code);
      await login({
        email,
        password,
        signIn,
        history,
        setUser,
        setHasCompletedOnboarding,
        setActiveStep,
      });
      trackEmailVerified(trackEvent);
      saveSignUpData();
    } catch (error) {
      snackbar.show(error.message, 'error');
    }
  }

  return (
    <div className={classes.root}>
      {signup && (
        <>
          <Typography variant="h5" className={classes.header}>
            Great job.
          </Typography>
          <Typography variant="h5" className={classes.header} paragraph>
            You've signed up to {companyInfo[theme].company}.
          </Typography>
        </>
      )}
      {!signup && (
        <Typography variant="h5" className={classes.header} paragraph>
          Email verification
        </Typography>
      )}
      <Typography className={classes.subHeader}>
        Let's verify your email before continuing. A verification code has been
        sent to your email address.
      </Typography>
      <Formik
        initialValues={{ code: '' }}
        validationSchema={yup.object({
          code: yup.number().required(),
        })}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => {
          return (
            <Form>
              <Typography className={classes.inputLabel}>
                Verification code
              </Typography>
              <Field
                component={TextField}
                variant="outlined"
                name="code"
                type="text"
                fullWidth
                className={classes.field}
              />
              <LoadingButton
                loading={isSubmitting}
                fullWidth
                type="submit"
                className={classes.verifyEmailButton}
              >
                Verify email
              </LoadingButton>
              <Typography className={classes.resendCode}>
                Didn't receive a verification code?{' '}
                <Link onClick={() => resendVerifyEmailCode(email, snackbar)}>
                  Resend code
                </Link>
              </Typography>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}
